import { supabase } from '@/integrations/supabase/client';
import { Mesa, MesaForma } from '@/types';

// The generated `Database` type doesn't yet know about `mesas` or about the
// new invitados columns added in 20260514180000_mesas_seating.sql. Until
// `supabase gen types typescript` is re-run, services that touch those rows
// cast through `unknown`. Mirrors the pattern used by useSubscription /
// PlansAdmin for the plans + subscriptions tables.

interface MesaRow {
  id: string;
  evento_id: string;
  numero: number;
  nombre: string | null;
  capacidad: number;
  forma: MesaForma;
  orden: number;
  created_at: string;
  updated_at: string;
}

type MesaInsert = Omit<MesaRow, 'id' | 'created_at' | 'updated_at'> & {
  id?: string;
};
type MesaUpdate = Partial<Omit<MesaRow, 'id' | 'evento_id' | 'created_at' | 'updated_at'>>;

interface UntypedQueryError {
  code?: string;
  message?: string;
}

interface UntypedClient {
  from: (table: string) => {
    select: (cols?: string) => {
      eq: (col: string, value: unknown) => {
        order: (col: string, opts?: { ascending?: boolean }) => {
          order: (col: string, opts?: { ascending?: boolean }) => Promise<{
            data: MesaRow[] | null;
            error: UntypedQueryError | null;
          }>;
        };
      };
    };
    insert: (rows: MesaInsert | MesaInsert[]) => {
      select: () => Promise<{ data: MesaRow[] | null; error: UntypedQueryError | null }> & {
        single: () => Promise<{ data: MesaRow | null; error: UntypedQueryError | null }>;
      };
    };
    update: (patch: MesaUpdate) => {
      eq: (col: string, value: unknown) => {
        select: () => {
          single: () => Promise<{ data: MesaRow | null; error: UntypedQueryError | null }>;
        };
      };
    };
    delete: () => {
      eq: (col: string, value: unknown) => Promise<{ error: UntypedQueryError | null }>;
    };
    upsert: (rows: Array<{ id: string; orden: number }>) => Promise<{
      error: UntypedQueryError | null;
    }>;
  };
}

const client = supabase as unknown as UntypedClient;

const rowToMesa = (row: MesaRow): Mesa => ({
  id: row.id,
  evento_id: row.evento_id,
  numero: row.numero,
  nombre: row.nombre,
  capacidad: row.capacidad,
  forma: row.forma,
  orden: row.orden,
});

export interface BulkCreateMesasInput {
  cantidad: number;
  capacidad: number;
  forma?: MesaForma;
  startNumero?: number;
  startOrden?: number;
}

export interface CreateMesaInput {
  numero: number;
  capacidad: number;
  forma?: MesaForma;
  nombre?: string | null;
  orden?: number;
}

export const mesaService = {
  getEventMesas: async (eventId: string): Promise<Mesa[]> => {
    const { data, error } = await client
      .from('mesas')
      .select('*')
      .eq('evento_id', eventId)
      .order('orden', { ascending: true })
      .order('numero', { ascending: true });

    if (error) throw new Error(error.message ?? 'Error cargando mesas');
    return (data ?? []).map(rowToMesa);
  },

  createMesa: async (input: CreateMesaInput, eventId: string): Promise<Mesa> => {
    const insert: MesaInsert = {
      evento_id: eventId,
      numero: input.numero,
      nombre: input.nombre ?? null,
      capacidad: input.capacidad,
      forma: input.forma ?? 'REDONDA',
      orden: input.orden ?? input.numero,
    };

    const { data, error } = await client.from('mesas').insert(insert).select().single();
    if (error || !data) throw new Error(error?.message ?? 'No se pudo crear la mesa');
    return rowToMesa(data);
  },

  bulkCreateMesas: async (
    input: BulkCreateMesasInput,
    eventId: string,
  ): Promise<Mesa[]> => {
    if (input.cantidad <= 0) return [];

    const startNumero = input.startNumero ?? 1;
    const startOrden = input.startOrden ?? startNumero;
    const inserts: MesaInsert[] = Array.from({ length: input.cantidad }, (_, i) => ({
      evento_id: eventId,
      numero: startNumero + i,
      nombre: null,
      capacidad: input.capacidad,
      forma: input.forma ?? 'REDONDA',
      orden: startOrden + i,
    }));

    const { data, error } = await client.from('mesas').insert(inserts).select();
    if (error) throw new Error(error.message ?? 'No se pudieron crear las mesas');
    return (data ?? []).map(rowToMesa);
  },

  updateMesa: async (id: string, patch: MesaUpdate): Promise<Mesa> => {
    const { data, error } = await client
      .from('mesas')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error || !data) throw new Error(error?.message ?? 'No se pudo actualizar la mesa');
    return rowToMesa(data);
  },

  deleteMesa: async (id: string): Promise<void> => {
    const { error } = await client.from('mesas').delete().eq('id', id);
    if (error) throw new Error(error.message ?? 'No se pudo eliminar la mesa');
  },

  /**
   * Re-numbers `orden` based on the array order. Pass the new sequence of
   * mesa IDs after a drag-reorder; this fires a single upsert call.
   */
  reorderMesas: async (orderedIds: string[]): Promise<void> => {
    if (orderedIds.length === 0) return;
    const rows = orderedIds.map((id, idx) => ({ id, orden: idx }));
    const { error } = await client.from('mesas').upsert(rows);
    if (error) throw new Error(error.message ?? 'No se pudo reordenar');
  },
};
